import * as _ from 'lodash';
import * as Files from './files';
import * as MediaImage from './mediaImage';
import * as MediaTable from './mediaTable';
import * as MediaVideo from './mediaVideo';
import * as MediaAudio from './mediaAudio';
import * as MediaHTML from './mediaHTML';
import * as MediaBokeh from './mediaBokeh';
import * as MediaObject3D from './mediaObject3D';

export type BasicType =
  | 'void'
  | 'any'
  | 'unknown'
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'table-row'
  | 'artifact'
  | 'run'
  | 'project'
  | 'org'
  | 'user'
  | 'filter'
  | null;

export const HANDLED_MEDIA_TYPES = [
  'image-file',
  'video-file',
  'audio-file',
  'html-file',
  'bokeh-file',
  'object3D-file',
  'table',
  'joined-table',
  'partitioned-table',
] as const;
export type MediaType = typeof HANDLED_MEDIA_TYPES[number];

export const isMediaType = (type: string): type is MediaType => {
  return HANDLED_MEDIA_TYPES.includes(type as any);
};

export type SimpleType = BasicType | MediaType;

export const SORTABLE_TYPES = ['string', 'number', 'boolean'];

export function isSimpleType(t: Type): t is SimpleType {
  return typeof t === 'string' || t === null;
}

// TODO: figure out how this merges with dict
export interface ObjectType {
  type: 'object';
  propertyTypes: {[key: string]: Type};
}

export interface Dict {
  type: 'dict';
  objectType: Type;
}

export interface TaggedValue {
  type: 'tagged';
  tag: Type;
  value: Type;
}

export interface ArrayType {
  type: 'array';
  objectType: Type;
}

// Used to denote support for any dimension of object.
// 0d: obj
// 1d: Array<obj>
// 2d: Array<Array<obj>>
// ...
export interface Tensor {
  type: 'tensor';
  objectType: Type;
}

export interface TableType {
  type: 'table';
  columnTypes: {[key: string]: Type};
}

export interface JoinedTableType {
  type: 'joined-table';
  columnTypes: {[key: string]: Type};
}

export interface PartitionedTableType {
  type: 'partitioned-table';
  columnTypes: {[key: string]: Type};
}

export interface FunctionType {
  type: 'function';
  inputType: Type;
  outputType: Type;
}

// Unused
export interface Query {
  type: 'query';
  resultType: Type;
}

export interface File {
  type: 'file';
  extension?: string;
  wbObjectType?: MediaType;
}

export interface Artifact {
  type: 'artifact';
}

export interface Dir {
  type: 'dir';
}

export interface TableInfo {
  type: 'table-info';
}

export interface Union {
  type: 'union';
  members: Type[];
}

export type QueryPath = Files.ObjectId;

export type Type =
  | ObjectType
  | ArrayType
  | Tensor
  | Dict
  | TaggedValue
  | TableType
  | JoinedTableType
  | PartitionedTableType
  | SimpleType
  | MediaType
  | Query
  | Dir
  | File
  | Artifact
  | TableInfo
  | FunctionType
  | Union;

// Convert a Type.Type to an actual Typescript type that matches it!
export type TypeToTSTypeInner<T> = T extends 'any'
  ? Type
  : T extends 'project'
  ? any // TODO
  : T extends 'run'
  ? any // TODO
  : T extends 'filter'
  ? any // TODO
  : T extends 'string'
  ? string
  : T extends 'number'
  ? number
  : T extends 'string-unwrapped'
  ? string
  : T extends 'boolean'
  ? boolean
  : T extends 'date'
  ? Date
  : T extends 'unknown'
  ? unknown
  : T extends 'image-file'
  ? MediaImage.WBImage
  : T extends 'video-file'
  ? MediaVideo.WBVideo
  : T extends 'audio-file'
  ? MediaAudio.WBAudio
  : T extends 'html-file'
  ? MediaHTML.WBHtml
  : T extends 'bokeh-file'
  ? MediaBokeh.WBBokeh
  : T extends 'object3D-file'
  ? MediaObject3D.Object3D
  : T extends 'table'
  ? MediaTable.MediaTable
  : T extends File
  ? Files.LoadedPathMetadata
  : T extends Artifact
  ? Files.ObjectId
  : T extends Dir
  ? Files.LoadedPathMetadata
  : T extends Dict
  ? DictTSType<T['objectType']>
  : T extends ArrayType
  ? ArrayTsType<T['objectType']>
  : T extends Union // Use a mapped type to distribute TypeToTSType over the members array, // then use [number] lookup on the array to get a union, which produces // a union of the array's members. (this basically says "give me the type // that would result from indexing the array with any number").
  ? {[K in keyof T['members']]: TypeToTSTypeInner<T['members'][K]>}[number]
  : never;

type DictTSType<T> = Array<{
  key: string;
  value: TypeToTSTypeInner<T>;
}>;

type ArrayTsType<T> = Array<TypeToTSTypeInner<T>>;

export function toString(type: Type): string {
  if (type === undefined) {
    return 'INVALID_TYPE_ERROR';
  }
  if (isSimpleType(type)) {
    return type ?? 'null';
  } else if (type.type === 'object') {
    return `{${_.map(type.propertyTypes, (v, k) => k + ': ' + toString(v)).join(
      ', '
    )}}`;
  } else if (type.type === 'table') {
    return `Table<{${_.map(
      type.columnTypes,
      (v, k) => k + ': ' + toString(v)
    ).join(', ')}}>`;
  } else if (type.type === 'dict') {
    return `{[k]: ${toString(type.objectType)}}`;
  } else if (type.type === 'tagged') {
    return `Tagged<${toString(type.tag)}, ${toString(type.value)}>`;
  } else if (type.type === 'array') {
    if (!isSimpleType(type.objectType) && type.objectType.type === 'union') {
      return `Array<${toString(type.objectType)}>`;
    }
    return `${toString(type.objectType)}[]`;
  } else if (type.type === 'file') {
    return `File<{${
      type.wbObjectType != null
        ? 'wbObjectType: ' + type.wbObjectType + ', '
        : ''
    }${type.extension != null ? 'extension: ' + type.extension : ''}}>`;
  } else if (type.type === 'query') {
    return `Query<${toString(type.resultType)}>`;
  } else if (type.type === 'dir') {
    return 'Dir';
  } else if (type.type === 'union') {
    return type.members.map(toString).join(' | ');
  } else if (type.type === 'table-info') {
    return 'TableInfo';
  }
  throw new Error(`unhandled type ${JSON.stringify(type)}`);
}

export function isAssignableTo(type: Type, toType: Type): boolean {
  return isAssignableTo2(type, toType);
}

export function isAssignableTo2(type: Type, toType: Type): boolean {
  if (isUnion(type)) {
    // Union to union assignment. Each member in type must be assignable
    // to at least one member of toType.
    return type.members.every(typeMemberType =>
      isAssignableTo2(typeMemberType, toType)
    );
  } else if (isTaggedValue(type)) {
    return isAssignableTo2(type.value, toType);
  }
  if (toType === 'any') {
    return true;
  } else if (isSimpleType(type) && isSimpleType(toType)) {
    return type === toType;
  } else if (!isSimpleType(type) && !isSimpleType(toType)) {
    if (type.type === 'dict' && toType.type === 'dict') {
      return isAssignableTo2(type.objectType, toType.objectType);
    } else if (type.type === 'table' && toType.type === 'table') {
      // TODO: not true! Check column types!
      return true;
    } else if (type.type === 'object' && toType.type === 'object') {
      // TODO: not true! Check column types!
      return true;
    } else if (type.type === 'array' && toType.type === 'array') {
      return isAssignableTo2(type.objectType, toType.objectType);
    } else if (type.type === 'file' && toType.type === 'file') {
      // If either extension or wbObjectType matches (or none are specified)
      // return true. (This is the same structural matching typescript does).
      if (toType.wbObjectType == null && toType.extension == null) {
        return true;
      }
      if (
        toType.wbObjectType != null &&
        type.wbObjectType === toType.wbObjectType
      ) {
        return true;
      }
      if (toType.extension != null && type.extension === toType.extension) {
        return true;
      }
      // Specified extension didn't match and specified wbObjectType didn't match
      return false;
    } else if (type.type === 'query' && toType.type === 'query') {
      return isAssignableTo2(type.resultType, toType.resultType);
    } else if (type.type === 'dir' && toType.type === 'dir') {
      return true;
    }
  }
  if (isTensor(toType)) {
    while (!isSimpleType(type) && type.type === 'array') {
      if (isAssignableTo2(type, toType.objectType)) {
        return true;
      }
      type = type.objectType;
    }
    return isAssignableTo2(type, toType.objectType);
  }
  if (isUnion(toType)) {
    // Note, this doesn't match union of unions
    return toType.members.some(fitMemberType =>
      isAssignableTo2(type, fitMemberType)
    );
  }
  return false;
}

// TODO: reorder this file. Intersperse each type defintion with its constructor
// function and type guard function.

// Only squashes one level of union right now

export function maybe(t: Type): Type {
  return union([null, t]);
}

// If a type is a maybe, get the inner type.
export function nonNullable(t: Type): Type {
  if (isUnion(t)) {
    return union(t.members.filter(memberType => memberType !== null));
  }
  return t;
}

export function isNullable(t: Type): boolean {
  return isUnion(t) && t.members.find(m => m === null) != null;
}

export function union(members: Type[]): Type {
  let allMembers: Type[] = [];
  for (const mem of members) {
    if (!isSimpleType(mem) && mem.type === 'union') {
      allMembers = allMembers.concat(mem.members);
    } else {
      allMembers.push(mem);
    }
  }
  if (allMembers.length === 1) {
    return allMembers[0];
  }
  return {
    type: 'union' as const,
    // This uniq could be more powerful (merge by any types that are assignable to
    // other types)
    // TODO
    members: _.uniqWith(allMembers, _.isEqual),
  };
}

export function isTaggedValue(t: Type): t is TaggedValue {
  return !isSimpleType(t) && t.type === 'tagged';
}

export function taggedValue(tagType: Type, valueType: Type): Type {
  return {
    type: 'tagged',
    tag: tagType,
    value: valueType,
  };
}

export function isUnion(t: Type): t is Union {
  return !isSimpleType(t) && t.type === 'union';
}

export function isTensor(t: Type): t is Tensor {
  return !isSimpleType(t) && t.type === 'tensor';
}

export function tensor(objectType: Type): Type {
  return {
    type: 'tensor',
    objectType,
  };
}

export function isObject(t: Type): t is ObjectType {
  return !isSimpleType(t) && t.type === 'object';
}

export function object(propertyTypes: {[key: string]: Type}): ObjectType {
  return {
    type: 'object',
    propertyTypes,
  };
}

export function isArray(t: Type): t is Tensor {
  return !isSimpleType(t) && t.type === 'array';
}

export function array(objectType: Type): Type {
  return {
    type: 'array',
    objectType,
  };
}

export function oneOrMany(objectType: Type): Type {
  return union([array(objectType), objectType]);
}

export const canSortType = (type?: Type): boolean => {
  if (type) {
    // If the type is a "maybe", then support it.
    if ((type as Union).members) {
      const firstNonNull = (type as Union).members.find(t => t !== null);
      return (
        !!firstNonNull &&
        canSortType(firstNonNull) &&
        (type as Union).members.every(t => t === null || t === firstNonNull)
      );
    } else {
      return SORTABLE_TYPES.includes(type.toString());
    }
  }
  return false;
};
